// src/pages/lab/labOrders/components/LabOrderTable.tsx
import React, { useState } from "react";
import { Table, Tag, Button, Space } from "antd";
import type { ColumnsType, TableProps } from "antd/es/table";
import { TruckOutlined, LinkOutlined, SendOutlined, PlusCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { LabOrder, LabOrderItem, parseTestType, LabOrderStatusOptions } from "../../../../types/lab/LabOrderTypes";
import { SampleAnalysis } from "../../../../types/lab/SampleTypes";
import ShippingTrackingDrawer from "./ShippingTrackingDrawer";
import { StatusTag } from "../../../../components/shared";

interface LabOrderTableProps {
	orders: LabOrder[];
	totalItems: number;
	currentPage: number;
	loading?: boolean;
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
	selectedOrders: number[];
	setSelectedOrders: React.Dispatch<React.SetStateAction<number[]>>;
	onEditOrder: (orderId: number) => void;
	itemsPerPage: number;
	onPageChange: (page: number) => void;
	handleDeliverResults: (order: LabOrder, analysis?: SampleAnalysis) => void;
	expandedRows: any;
	setExpandedRows: (rows: any) => void;
	expandedOrderItems: any;
	setExpandedOrderItems: (rows: any) => void;
	onCreateSample: (order: LabOrder, orderItemId: number) => void;
	onOpenSampleModal: (order: LabOrder, orderItemId: number) => void;
	handleFilterStatus?: (status: string | null) => void;
}

const LabOrderTable: React.FC<LabOrderTableProps> = ({
	orders,
	totalItems,
	currentPage,
	loading = false,
	setCurrentPage,
	selectedOrders,
	setSelectedOrders,
	onEditOrder,
	itemsPerPage,
	onPageChange,
	handleDeliverResults,
	expandedRows,
	setExpandedRows,
	expandedOrderItems,
	setExpandedOrderItems,
	onCreateSample,
	onOpenSampleModal,
	handleFilterStatus,
}) => {
	const [shippingDrawerOpen, setShippingDrawerOpen] = useState(false);
	const [selectedOrderForShipping, setSelectedOrderForShipping] = useState<LabOrder | null>(null);
	const [creatingBulkSamples, setCreatingBulkSamples] = useState<{ [key: number]: boolean }>({});
	const [statusFilter, setStatusFilter] = useState<string | null>(null);

	const handleCreateAllSamples = async (order: LabOrder, event: React.MouseEvent) => {
		event.stopPropagation();

		if (!order.order_items || order.order_items.length === 0) return;

		setCreatingBulkSamples((prev) => ({ ...prev, [order.id]: true }));

		try {
			const itemsNeedingSamples = order.order_items.filter((item) => item.sample_id && !item.sample);

			for (const item of itemsNeedingSamples) {
				await onCreateSample(order, item.id);
			}
		} finally {
			setCreatingBulkSamples((prev) => ({ ...prev, [order.id]: false }));
		}
	};

	const hasItemsWithoutSamples = (order: LabOrder): boolean => {
		if (!order.order_items || order.order_items.length === 0) return false;
		return order.order_items.some((item) => item.sample_id && !item.sample);
	};

	const columns: ColumnsType<LabOrder> = [
		{
			title: "Order ID",
			dataIndex: "order_number",
			key: "order_number",
		},
		{
			title: "Ordered",
			dataIndex: "ordered_at",
			key: "ordered_at",
			render: (date: string) =>
				date
					? new Date(date).toLocaleDateString("en-US", {
						month: "short",
						day: "numeric",
						year: "numeric",
						hour: "2-digit",
						minute: "2-digit",
					})
					: "—",
		},
		{
			title: "Priority",
			dataIndex: "priority",
			key: "priority",
			render: (priority: string) => <StatusTag status={priority} size="small" />,
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			filterMultiple: false,
			filters: LabOrderStatusOptions.map((option) => ({ text: option.label, value: option.value })),
			filteredValue: statusFilter ? [statusFilter] : null,
			render: (status: string) => <StatusTag status={status} size="small" />,
		},
		{
			title: "Shipping",
			dataIndex: "shipping_status",
			key: "shipping_status",
			render: (status: string) =>
				status ? (
					<StatusTag status={status} size="small" />
				) : (
					<span style={{ color: "var(--mg-text-disabled)" }}>Not Set</span>
				),
		},
		{
			title: "Received",
			dataIndex: "received_at",
			key: "received_at",
			render: (date: string) =>
				date
					? new Date(date).toLocaleDateString("en-US", {
						month: "short",
						day: "numeric",
						year: "numeric",
						hour: "2-digit",
						minute: "2-digit",
					})
					: "—",
		},
		{
			title: "Actions",
			key: "actions",
			align: "center",
			render: (_, record: LabOrder) => (
				<div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
					<Button
						type="text"
						size="small"
						icon={<TruckOutlined />}
						onClick={(e) => {
							e.stopPropagation();
							setSelectedOrderForShipping(record);
							setShippingDrawerOpen(true);
						}}
						title="Shipping Tracking"
					/>
					{hasItemsWithoutSamples(record) && (
						<Button
							type="text"
							size="small"
							icon={creatingBulkSamples[record.id] ? <LoadingOutlined /> : <LinkOutlined />}
							onClick={(e) => handleCreateAllSamples(record, e)}
							disabled={creatingBulkSamples[record.id]}
							title="Create All Sample Links"
							style={{ color: "var(--mg-warning)" }}
						/>
					)}
					{["completed", "results_ready", "reviewed"].includes(record.status) && (
						<Button
							type="text"
							size="small"
							icon={<SendOutlined />}
							onClick={(e) => {
								e.stopPropagation();
								handleDeliverResults(record);
							}}
							title="Deliver Results"
							style={{ color: "var(--mg-success)" }}
						/>
					)}
				</div>
			),
		},
	];

	const expandedRowRender = (order: LabOrder) => {
		if (!order.order_items || order.order_items.length === 0) {
			return <div style={{ padding: 12, color: "var(--mg-text-secondary)" }}>No order items</div>;
		}

		// Third-level expansion renderer for analyses
		const analysesExpandedRowRender = (orderItem: LabOrderItem) => {
			if (!orderItem.sample?.analyses || orderItem.sample.analyses.length === 0) {
				return <div style={{ padding: 12, color: "var(--mg-text-secondary)" }}>No analyses available</div>;
			}

			const analysesColumns: ColumnsType<SampleAnalysis> = [
				{
					title: "Analysis ID",
					dataIndex: "analysis_id",
					key: "analysis_id",
					width: 120,
				},
				{
					title: "Protocol Name",
					dataIndex: "protocol_name",
					key: "protocol_name",
					width: 150,
				},
				{
					title: "Status",
					dataIndex: "status",
					key: "status",
					width: 120,
					render: (status: string) => <StatusTag status={status} size="small" />,
				},
				{
					title: "Assigned At",
					dataIndex: "assigned_at",
					key: "assigned_at",
					width: 140,
					render: (date: string) =>
						date
							? new Date(date).toLocaleDateString("en-US", {
								month: "short",
								day: "numeric",
								year: "numeric",
								hour: "2-digit",
								minute: "2-digit",
							})
							: "—",
				},
				{
					title: "Completed At",
					dataIndex: "completed_at",
					key: "completed_at",
					width: 140,
					render: (date: string) =>
						date
							? new Date(date).toLocaleDateString("en-US", {
								month: "short",
								day: "numeric",
								year: "numeric",
								hour: "2-digit",
								minute: "2-digit",
							})
							: "—",
				},
				{
					title: "Actions",
					key: "actions",
					width: 120,
					align: "center",
					render: (_, analysis: SampleAnalysis) => {
						if (analysis.status === "completed") {
							return (
								<Button
									type="text"
									size="small"
									icon={<SendOutlined />}
									onClick={(e) => {
										e.stopPropagation();
										handleDeliverResults(order, analysis);
									}}
									title="Deliver Analysis Results"
									style={{ color: "var(--mg-success)" }}
								/>
							);
						}
						return null;
					},
				},
			];

			return <Table columns={analysesColumns} dataSource={orderItem.sample.analyses} size="small" pagination={false} rowKey="id" scroll={{ x: "max-content" }} />;
		};

		const expandColumns: ColumnsType<LabOrderItem> = [
			{
				title: "Sample ID",
				dataIndex: "sample_id",
				key: "sample_id",
				width: 150,
			},
			{
				title: "Sample Type",
				dataIndex: "sample_type",
				key: "sample_type",
				width: 150,
			},
			{
				title: "Test Category",
				dataIndex: "test_category",
				key: "test_category",
				width: 150,
			},
			{
				title: "Test Types",
				dataIndex: "test_type",
				key: "test_type",
				width: 250,
				render: (testType: string) => {
					const testTypes = parseTestType(testType);
					if (testTypes.length === 0) return <span style={{ color: "#999" }}>None</span>;
					return (
						<Space size={[4, 4]} wrap>
							{testTypes.map((type, index) => (
								<Tag key={index} color="blue" style={{ fontSize: 11, margin: 0 }}>
									{type}
								</Tag>
							))}
						</Space>
					);
				},
			},
			{
				title: "Status",
				dataIndex: "status",
				key: "status",
				width: 120,
				render: (status: string) => <StatusTag status={status} size="small" />,
			},
			{
				title: "Received At",
				dataIndex: "received_at",
				key: "received_at",
				width: 180,
				render: (date: string) =>
					date
						? new Date(date).toLocaleDateString("en-US", {
							month: "short",
							day: "numeric",
							year: "numeric",
							hour: "2-digit",
							minute: "2-digit",
						})
						: "—",
			},
			{
				title: "Sample Status",
				key: "sample_status",
				width: 120,
				render: (_, item: LabOrderItem) => {
					if (!item.sample_id) return <span style={{ color: "var(--mg-text-disabled)" }}>No Sample ID</span>;
					const exists = item.sample ?? false;
					return exists ? (
						<StatusTag status="completed" label="Linked" size="small" />
					) : (
						<StatusTag status="pending_review" label="Not Created" size="small" />
					);
				},
			},
			{
				title: "Analyses",
				key: "analyses",
				width: 100,
				render: (_, item: LabOrderItem) => {
					const analysesCount = item.sample?.analyses?.length || 0;
					return (
						<span style={{ fontSize: 11, color: "var(--mg-text-secondary)" }}>
							{analysesCount} {analysesCount !== 1 ? "analyses" : "analysis"}
						</span>
					);
				},
			},
			{
				title: "Actions",
				key: "actions",
				width: 120,
				align: "center",
				render: (_, item: LabOrderItem) => {
					if (!item.sample_id) return null;
					const exists = item.sample ?? false;

					return !exists ? (
						<div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
							<Button
								type="text"
								size="small"
								icon={<LinkOutlined />}
								onClick={(e) => {
									e.stopPropagation();
									onCreateSample(order, item.id);
								}}
								title="Quick Create Sample"
							/>
							<Button
								type="text"
								size="small"
								icon={<PlusCircleOutlined />}
								onClick={(e) => {
									e.stopPropagation();
									onOpenSampleModal(order, item.id);
								}}
								title="Create Sample with Details"
								style={{ color: "var(--mg-success)" }}
							/>
						</div>
					) : null;
				},
			},
		];

		return (
			<Table
				columns={expandColumns}
				dataSource={order.order_items}
				size="small"
				pagination={false}
				rowKey="id"
				scroll={{ x: "max-content" }}
				expandable={{
					expandedRowRender: analysesExpandedRowRender,
					expandedRowKeys: expandedOrderItems ? Object.keys(expandedOrderItems).map(Number).filter(key => expandedOrderItems[key]) : [],
					onExpandedRowsChange: (keys) => {
						const newExpandedOrderItems: any = {};
						keys.forEach((key) => {
							newExpandedOrderItems[Number(key)] = true;
						});
						setExpandedOrderItems(newExpandedOrderItems);
					},
					rowExpandable: (record) => (record.sample?.analyses?.length || 0) > 0,
				}}
			/>
		);
	};

	const handleShippingDrawerClose = () => {
		setShippingDrawerOpen(false);
		setSelectedOrderForShipping(null);
		onPageChange(currentPage);
	};

	const rowSelection = {
		selectedRowKeys: selectedOrders,
		onChange: (selectedRowKeys: React.Key[]) => {
			setSelectedOrders(selectedRowKeys as number[]);
		},
	};

	const handleTableChange: TableProps<LabOrder>["onChange"] = (_pagination, filters) => {
		const next = filters.status && filters.status.length > 0 ? String(filters.status[0]) : null;
		if (next === statusFilter) return;
		setStatusFilter(next);
		handleFilterStatus && handleFilterStatus(next);
	};

	return (
		<>
			<Table
				data-testid="orders-table"
				columns={columns}
				dataSource={orders}
				rowKey="id"
				size="small"
				loading={loading}
				rowSelection={rowSelection}
				expandable={{
					expandedRowRender,
					expandedRowKeys: expandedRows ? Object.keys(expandedRows).map(Number) : [],
					onExpandedRowsChange: (keys) => {
						const newExpandedRows: any = {};
						keys.forEach((key) => {
							newExpandedRows[Number(key)] = true;
						});
						setExpandedRows(newExpandedRows);
					},
				}}
				pagination={{
					current: currentPage + 1,
					pageSize: itemsPerPage,
					total: totalItems,
					onChange: (page) => setCurrentPage(page - 1),
					showTotal: (total) => `Total ${total} orders`,
					size: "small",
					showSizeChanger: false,
				}}
				scroll={{ y: "calc(100vh - 250px)", x: "max-content" }}
				onChange={handleTableChange}
				onRow={(record) => ({
					onClick: () => onEditOrder(record.id),
					style: { cursor: "pointer" },
				})}
			/>

			{/* Shipping Tracking Drawer */}
			{shippingDrawerOpen && selectedOrderForShipping && (
				<ShippingTrackingDrawer isOpen={shippingDrawerOpen} onClose={handleShippingDrawerClose} order={selectedOrderForShipping} onOrderUpdate={handleShippingDrawerClose} />
			)}
		</>
	);
};

export default LabOrderTable;
